import { createEvent, createStore } from 'effector'

import { HIDE_ANIMATION_DURATION } from '@/dialogs/constants.ts'
import { type DialogEventPayload } from '@/dialogs/declarations.tsx'
import { delay } from '@/utils/misc.ts'

let counter = 0

/**
 * Only for unit tests.
 */
export function resetCounter() {
  counter = 0
}

export type DialogModel = DialogEventPayload & {
  id: number
  isOpen: boolean
  onClose: () => void
}

export const openDialog = createEvent<DialogEventPayload>('open dialog')
export const hideDialog = createEvent<number>('hide dialog by id')
/**
 * @internal Shouldn't be used manually.
 */
export const deleteDialog = createEvent<number>('delete dialog by id')

export const $openedDialogs = createStore<DialogModel[]>([], {
  name: 'dialogs',
})
  .on(openDialog, (store, data) => {
    const id = counter++
    const onClose = () => {
      hideDialog(id)
    }

    return store
      .map((item) =>
        item.isOpen
          ? {
              ...item,
              isOpen: item.isPersistent || false,
            }
          : item,
      )
      .concat({
        ...data,
        id,
        onClose,
        isOpen: true,
      })
  })
  .on(hideDialog, (store, idToRemove) => {
    const dialogBeforeHidedIndex =
      store.findIndex(({ id }) => id === idToRemove) - 1

    return store.map((item, index) => {
      const isOpen =
        dialogBeforeHidedIndex === index
          ? true
          : idToRemove === item.id
            ? false
            : item.isOpen

      return item.isOpen !== isOpen
        ? {
            ...item,
            isOpen,
          }
        : item
    })
  })
  .on(deleteDialog, (store, idToRemove) =>
    store.filter(({ id }) => id !== idToRemove),
  )

hideDialog.watch(async (idToRemove) => {
  // after hide we need to delete dialogs from virtual dom, 200ms it is duration of hide animation
  await delay(HIDE_ANIMATION_DURATION)
  deleteDialog(idToRemove)
})

/**
 * Kind of hack, because events cannot return anything except ifself
 */
export const getLastDialogId = () => {
  // eslint-disable-next-line effector/no-getState
  const dialogs = $openedDialogs.getState()

  return dialogs[dialogs.length - 1]?.id
}
